import type { CursorPaginatedResponse, CursorPaginationParams } from '@/libs/interfaces'

import { MOCK_LATENCY_MS } from './config'

export const delay = <T>(value: T, ms: number = MOCK_LATENCY_MS): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms))

let idCounter = 0
export const genID = (prefix: string): string => {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

export const nowISO = (): string => new Date().toISOString()

/** Index-based cursor pagination over an already-filtered/sorted array. */
export const paginate = <T>(
  items: T[],
  params: CursorPaginationParams = {},
): CursorPaginatedResponse<T> => {
  const limit = params.limit ?? items.length
  const startIndex = params.cursor ? Number(params.cursor) : 0
  const page = items.slice(startIndex, startIndex + limit)
  const nextIndex = startIndex + limit
  const lastIndex = Math.max(items.length - limit, 0)

  return {
    data: page,
    pageInfo: {
      first: '0',
      prev: startIndex > 0 ? String(Math.max(startIndex - limit, 0)) : undefined,
      cursor: String(startIndex),
      next: nextIndex < items.length ? String(nextIndex) : undefined,
      last: String(lastIndex),
    },
  }
}

/** Case-insensitive substring match across a set of string fields. */
export const matchesSearch = <T>(
  item: T,
  search: string | undefined,
  fields: (keyof T)[],
): boolean => {
  if (!search) return true
  const needle = search.toLowerCase()
  return fields.some((field) =>
    String(item[field] ?? '')
      .toLowerCase()
      .includes(needle),
  )
}
